import React, { useState, useEffect } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import api from '../api';
import API_URL from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import {
    DetailContainer,
    Header,
    ProjectInfo,
    Section,
    Checklist,
    ChecklistItem,
    Button,
    ButtonContainer,
    DeleteButton,
    CheckboxGroup,
} from './Detail.styles';
import { Vloerverwarming, Klimaatwand, Klimaatplafond, Project } from '../types';
import ProjectSectionForm from './ProjectSectionForm';
import logo from '../assets/logo.png';
import LoadingOverlay from './LoadingOverlay';

const EditProjectPage: React.FC = () => {
    const [project, setProject] = useState<Project | null>(null);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const response = await api.get(`/api/projects/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                setProject(response.data);
            } catch (error) {
                console.error('Error fetching project', error);
            }
        };
        fetchProject();
    }, [id]);

    if (!project) {
        return <div>Loading...</div>;
    }

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await api.delete(`/api/projects/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                navigate('/');
            } catch (error) {
                console.error('Error deleting project', error);
            }
        }
    };

    return (
        <DetailContainer>
            <Header>
                <div>
                    <img src={logo} alt="Apply Heating Logo" />
                </div>
                <h2>Projectrapport bewerken</h2>
            </Header>
            <Formik
                initialValues={project}
                validationSchema={Yup.object({
                    title: Yup.string().required('Required'),
                    klant: Yup.string().required('Required'),
                    adres: Yup.string().required('Required'),
                    info: Yup.string().required('Required'),
                })}
                onSubmit={async (values) => {
                    const formData = new FormData();
                    formData.append('title', values.title);
                    formData.append('klant', values.klant);
                    formData.append('adres', values.adres);
                    formData.append('info', values.info);
                    formData.append('vloerverwarming', JSON.stringify(values.vloerverwarming));
                    if (values.klimaatwand) {
                        formData.append('klimaatwand', values.klimaatwand);
                    }
                    if (values.klimaatplafond) {
                        formData.append('klimaatplafond', values.klimaatplafond);
                    }
                    formData.append('volgensLegplan', String(values.volgensLegplan));
                    formData.append('kringnummersAangegevenOpVerdelers', String(values.kringnummersAangegevenOpVerdelers));
                    formData.append('installatieDicht', String(values.installatieDicht));
                    
                    values.sections.forEach((section: any, index: number) => {
                        if (section.plan instanceof File) {
                            formData.append(`section_${index}_plan`, section.plan);
                        }
                        section.images.forEach((image: any) => {
                            if (image instanceof File) {
                                formData.append(`section_${index}_images`, image);
                            }
                        });
                    });

                    const sectionsForServer = values.sections.map((section: any) => {
                        const newSection = { ...section };
                        if (newSection.plan instanceof File) {
                            newSection.plan = null;
                        }
                        newSection.images = newSection.images.filter((image: any) => !(image instanceof File));
                        return newSection;
                    });

                    formData.append('sections', JSON.stringify(sectionsForServer));

                    try {
                        await api.put(`/api/projects/${id}`, formData, {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        navigate(`/detail/${id}`);
                    } catch (error) {
                        console.error('Error updating project', error);
                    }
                }}
            >
                {({ values, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting }) => (
                    <>
                        {isSubmitting && <LoadingOverlay />}
                        <Form onSubmit={handleSubmit}>
                            <ProjectInfo>
                            <p><strong>Klant:</strong> <input id="klant" name="klant" type="text" onChange={handleChange} onBlur={handleBlur} value={values.klant} /></p>
                            <p><strong>Adres:</strong> <input id="adres" name="adres" type="text" onChange={handleChange} onBlur={handleBlur} value={values.adres} /></p>
                            <p><strong>Info:</strong> <textarea id="info" name="info" onChange={handleChange} onBlur={handleBlur} value={values.info} /></p>
                        </ProjectInfo>
                        <Section>
                            <h4>VLOERVERWARMING:</h4>
                            <CheckboxGroup>
                                {Object.values(Vloerverwarming).map((value) => (
                                    <label key={value}>
                                        <input
                                            type="checkbox"
                                        name="vloerverwarming"
                                        value={value}
                                        checked={values.vloerverwarming.includes(value)}
                                        onChange={(e) => {
                                            const { checked } = e.target;
                                            const newValues = checked
                                                ? [...values.vloerverwarming, value]
                                                : values.vloerverwarming.filter((v) => v !== value);
                                            setFieldValue('vloerverwarming', newValues);
                                        }}
                                        onBlur={handleBlur}
                                        />
                                        <span>{value}</span>
                                    </label>
                                ))}
                            </CheckboxGroup>
                        </Section>
                        <Section>
                            <h4>WANDKLIMAAT</h4>
                            <CheckboxGroup>
                                {Object.values(Klimaatwand).map((value) => (
                                    <label key={value}>
                                        <input
                                            type="checkbox"
                                        name="klimaatwand"
                                        value={value}
                                        checked={values.klimaatwand === value}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setFieldValue('klimaatwand', value);
                                            } else {
                                                setFieldValue('klimaatwand', '');
                                            }
                                        }}
                                        onBlur={handleBlur}
                                        />
                                        <span>{value}</span>
                                    </label>
                                ))}
                            </CheckboxGroup>
                        </Section>
                        <Section>
                            <h4>PLAFONDKLIMAAT</h4>
                            <CheckboxGroup>
                                {Object.values(Klimaatplafond).map((value) => (
                                    <label key={value}>
                                        <input
                                            type="checkbox"
                                        name="klimaatplafond"
                                        value={value}
                                        checked={values.klimaatplafond === value}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setFieldValue('klimaatplafond', value);
                                            } else {
                                                setFieldValue('klimaatplafond', '');
                                            }
                                        }}
                                        onBlur={handleBlur}
                                        />
                                        <span>{value}</span>
                                    </label>
                                ))}
                            </CheckboxGroup>
                        </Section>
                        <Checklist>
                            <h4>CHECKLIST</h4>
                            <ChecklistItem>
                                <span>Volgens legplan:</span>
                                <input type="checkbox" name="volgensLegplan" onChange={handleChange} onBlur={handleBlur} checked={values.volgensLegplan} />
                            </ChecklistItem>
                            <ChecklistItem>
                                <span>Kringnummers aangegeven op verdelers?</span>
                                <input type="checkbox" name="kringnummersAangegevenOpVerdelers" onChange={handleChange} onBlur={handleBlur} checked={values.kringnummersAangegevenOpVerdelers} />
                            </ChecklistItem>
                            <ChecklistItem>
                                <span>Installatie dicht:</span>
                                <input type="checkbox" name="installatieDicht" onChange={handleChange} onBlur={handleBlur} checked={values.installatieDicht} />
                            </ChecklistItem>
                        </Checklist>
                        <FieldArray
                            name="sections"
                            render={(arrayHelpers) => (
                                <div>
                                    {values.sections.map((section, index) => (
                                        <div key={index}>
                                            <ProjectSectionForm index={index} section={section} setFieldValue={setFieldValue} />
                                            <ButtonContainer>
                                                <Button type="button" onClick={() => arrayHelpers.remove(index)}>
                                                    Remove Section
                                                </Button>
                                            </ButtonContainer>
                                        </div>
                                    ))}
                                    <ButtonContainer>
                                        <Button
                                            type="button"
                                            onClick={() =>
                                                arrayHelpers.push({
                                                    sectionName: '',
                                                    afgeperstMet: '',
                                                    hoeveelheidDruk: 0,
                                                    buitenTemperatuur: 0,
                                                    plan: null,
                                                    images: [],
                                                })
                                            }
                                        >
                                            Add Section
                                        </Button>
                                    </ButtonContainer>
                                </div>
                            )}
                        />
                        <ButtonContainer>
                            <DeleteButton type="button" onClick={handleDelete}>Delete Project</DeleteButton>
                            <Button type="submit">Save Changes</Button>
                        </ButtonContainer>
                    </Form>
                </>
                )}
            </Formik>
        </DetailContainer>
    );
};

export default EditProjectPage;
